import { CommonModule } from '@angular/common';
import { Component, Renderer2 } from '@angular/core';
import { AccessibilityBarComponent } from '../accessibility-bar/accessibility-bar.component';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, AccessibilityBarComponent, RouterModule],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent {
  private isReading: boolean = false; // Tracks whether reading is active
  private utterance: SpeechSynthesisUtterance | null = null;

  isSearchActive = false;

  toggleSearch() {
    this.isSearchActive = !this.isSearchActive;
  }
  constructor(private renderer: Renderer2) {}

  // Function to toggle screen reading
  toggleScreenReading(): void {
    if (this.isReading) {
      // Stop reading
      window.speechSynthesis.cancel();
      this.isReading = false;
      this.clearHighlights();
    } else {
      // Start reading
      const content = document.body.innerText; // Get all visible text
      this.utterance = new SpeechSynthesisUtterance(content);
      this.utterance.lang = 'en-US';
      this.utterance.rate = 1;
      this.utterance.pitch = 1;

      // Highlight text as it's being read
      this.utterance.onboundary = (event: SpeechSynthesisEvent) => {
        this.highlightWord(
          content,
          event.charIndex,
          event.charIndex + event.charLength,
        );
      };

      // Handle the end of speech
      this.utterance.onend = () => {
        this.isReading = false;
        this.clearHighlights();
      };

      window.speechSynthesis.speak(this.utterance);
      this.isReading = true;
    }
  }

  // Highlight the word currently being spoken
  private highlightWord(text: string, start: number, end: number): void {
    this.clearHighlights(); // Remove previous highlights

    // Create a range for the spoken text
    const range = document.createRange();
    const nodes = Array.from(document.body.childNodes);
    let charCount = 0;

    for (const node of nodes) {
      const length = node.textContent?.length || 0;
      if (charCount + length >= start) {
        const offsetStart = start - charCount;
        const offsetEnd = Math.min(end - charCount, length);

        if (node.nodeType === Node.TEXT_NODE) {
          range.setStart(node, offsetStart);
          range.setEnd(node, offsetEnd);
        }

        const span = this.renderer.createElement('span');
        this.renderer.addClass(span, 'highlight');
        range.surroundContents(span);
        break;
      }

      charCount += length;
    }
  }

  // Clear all highlights
  private clearHighlights(): void {
    const highlights = document.querySelectorAll('.highlight');
    highlights.forEach((highlight) => {
      const parent = highlight.parentNode;
      while (highlight.firstChild) {
        parent?.insertBefore(highlight.firstChild, highlight);
      }
      parent?.removeChild(highlight);
    });
  }
}
